import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import AppConfig from '../../../lib/AppConfig';
import { RoleRepository } from '../repository/Roles';
import { Role } from '../types/response/Role';

interface RolesState {
	allRoles: Role[];
	displayedRoles: Role[];
	searchQuery: string;
	currentPage: number;
	pageSize: number;
	loading: boolean;
	error: string | null;
}

const initialState: RolesState = {
	allRoles: [],
	displayedRoles: [],
	searchQuery: '',
	currentPage: 0,
	pageSize: AppConfig.defaultPageSize,
	loading: false,
	error: null,
};

export const fetchRoles = createAsyncThunk('roles/fetchRoles', async (_, { rejectWithValue }) => {
	try {
		const roles = await RoleRepository.listRoles();
		return roles.sort((a, b) => (a.id! < b.id! ? 1 : 0));
	} catch (error: any) {
		return rejectWithValue(error.toString());
	}
});

const rolesSlice = createSlice({
	name: 'roles',
	initialState,
	reducers: {
		setSearchQuery(state, action: PayloadAction<string>) {
			state.searchQuery = action.payload;
		},
		setCurrentPage(state, action: PayloadAction<number>) {
			state.currentPage = action.payload;
		},
		setPageSize(state, action: PayloadAction<number>) {
			state.pageSize = action.payload;
		},
		updateDisplayedRoles(state) {
			const filtered = state.allRoles.filter(
				role =>
					role.name?.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
					role.description?.toLowerCase().includes(state.searchQuery.toLowerCase()),
			);
			const start = state.currentPage * state.pageSize;
			const end = start + state.pageSize;
			state.displayedRoles = filtered.slice(start, end);
		},
	},
	extraReducers: builder => {
		builder
			.addCase(fetchRoles.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchRoles.fulfilled, (state, action) => {
				state.allRoles = action.payload;
				state.currentPage = 0;
				state.searchQuery = '';
				const start = 0;
				const end = start + state.pageSize;
				state.displayedRoles = action.payload.slice(start, end);
				state.loading = false;
			})
			.addCase(fetchRoles.rejected, (state, action) => {
				state.error = action.payload as string;
				state.loading = false;
			});
	},
});

export const { setSearchQuery, setCurrentPage, setPageSize, updateDisplayedRoles } =
	rolesSlice.actions;

export default rolesSlice.reducer;
